import React from 'react';
import type { BundleId } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { WarrantyTag } from '../ui/WarrantyTag';
type Offer = {
  id: BundleId;
  badge: string;
  split: string;
  headline: string;
  body: string;
};
const OFFERS: Offer[] = [{
  id: 'sys8',
  badge: 'Most popular',
  split: '4 installed. 4 ready.',
  headline: 'Your complete weekly pool lighting system.',
  body: 'Designed for four permanent lighting positions and four charged backup lights. Enjoy your illuminated pool, exchange the set when charging is required, and recharge the removed units for the next rotation. Your installation locations stay fixed through every exchange.'
}, {
  id: 'sys24',
  badge: 'Best value',
  split: '12 installed. 12 ready.',
  headline: 'The complete lighting & backup system.',
  body: 'Built for twelve lighting positions with an additional twelve charged lights ready for easy rotation. Enjoy a larger illuminated space and the convenience of a full backup set.'
}];

/** Signature (primary) and Ultimate (premium) featured offers. */
export function FeaturedSystems() {
  const {
    lang
  } = useLanguage();
  const {
    startSystem
  } = useFunnel();
  const {
    setSelectedId
  } = useSystemSelection();
  const m = (n: number) => formatMoney(n, lang);
  return <section aria-labelledby="featured-title" className="border-t mf-hairline bg-deep" data-id="element-728" data-mp="ngdyiramrpe2">
      <div className="mx-auto max-w-site px-5 py-20 md:px-8 md:py-28" data-id="element-729" data-mp="ilrjme5cbep8">
        <h2 id="featured-title" className="max-w-3xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-730" data-mp="3r2ctry9pkdtx">
          One set installed. <span className="text-ice" data-id="element-731" data-mp="1q1g65cex7p9w">One set ready.</span>
        </h2>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.25fr_1fr]" data-id="element-732" data-mp="1pzc1gs976m8k">
          {OFFERS.map((o, i) => {
          const b = getBundle(o.id);
          const p = priceBundle(b);
          const primary = i === 0;
          return <article key={o.id} aria-labelledby={`offer-${o.id}`} className={`grid overflow-hidden border sm:grid-cols-[0.9fr_1fr] lg:grid-cols-1 xl:grid-cols-[0.9fr_1fr] ${primary ? 'mf-hud border-marine bg-[#0D2440]' : 'border-white/15 bg-[#06121F]'}`} data-id="element-733" data-mp="23tcq2t6fqsv7" data-mpi={i}>
                <img src={b.image} alt={`${b.name} — ${b.qty} M1 PRO lights`} loading="lazy" className="aspect-[1122/1402] w-full object-cover sm:h-full lg:h-auto xl:h-full" data-id="element-734" data-mp="1g5d2210idgkb" />
                <div className="flex flex-col p-6 md:p-8" data-id="element-735" data-mp="2rhvzxd54vdpe">
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${primary ? 'text-marine' : 'text-ice'}`} data-id="element-736" data-mp="2wgz650wunpyy">{o.badge}</p>
                  <h3 id={`offer-${o.id}`} className="mt-2 font-display text-3xl font-semibold uppercase leading-none tracking-[0.03em]" data-id="element-737" data-mp="36kxg05xv7ehn">
                    {b.tagline}
                  </h3>
                  <p className="mt-1 font-display text-lg uppercase tracking-[0.08em] text-titanium" data-id="element-738" data-mp="2wub52p13fc5a">
                    {b.qty} M1 PRO lights · <span className="text-white" data-id="element-739" data-mp="2sry2g9hn7ucq">{o.split}</span>
                  </p>
                  <p className="mt-5 font-display text-xl font-semibold uppercase leading-tight text-white" data-id="element-740" data-mp="2nwimnc02rodo">{o.headline}</p>
                  <p className="mt-3 text-[15px] leading-relaxed text-titanium" data-id="element-741" data-mp="2iy7er6tcbny3">{o.body}</p>

                  <div className="mt-auto pt-6" data-id="element-742" data-mp="2dyula37yrimj">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-743" data-mp="29vy32dmc5oj4">
                      <span className="font-display text-5xl font-semibold leading-none" data-id="element-744" data-mp="c5wuecxqz048">{m(p.total)}</span>
                      <span className="text-sm font-semibold uppercase tracking-[0.08em] text-ice" data-id="element-745" data-mp="83jsy0q19od9">Save {m(p.savings)}</span>
                    </div>
                    <p className="mt-1 text-sm text-titanium" data-id="element-746" data-mp="f4x0exzjqy7t">Only {m(p.perUnit)} per light</p>
                    <div className="mt-3" data-id="element-747" data-mp="k407sp301giy">
                      <WarrantyTag tier={b.warranty} linked data-id="element-748" data-mp="66uuc40yhfdo" />
                    </div>
                    <button type="button" onClick={() => {
                  track('featured_offer_click', {
                    bundleId: b.id,
                    value: p.total,
                    qty: b.qty
                  });
                  setSelectedId(b.id);
                  startSystem(b.id);
                }} className={`${buttonClasses(primary ? 'primary' : 'secondary', 'lg')} mt-5 w-full !whitespace-normal !px-4 text-center`} data-id="element-749" data-mp="a7n551ph7sc2">
                      Get my {b.tagline} — {m(p.total)}
                    </button>
                  </div>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}